import './WorkByEntity.css';
import * as React from 'react';
import { ActionButton } from 'office-ui-fabric-react';
import { Dictionary, EntityType, IEditableExtensibleEntityWithResourcePlan, entityTypeLabelMap } from '../../../entities/common';
import { formatValue, notUndefined, roundToHundreds, toDictionaryById } from '../../utils/common';
import { post } from '../../../fetch-interceptor';
import { defaultCatch } from '../../../store/utils';
import Link from '../../common/Link';

type OwnProps = {
    hoursByEntity: Dictionary<number>;
    entityType: EntityType;
};

export const WorkByEntity = (props: OwnProps) => {
    const { hoursByEntity, entityType } = props;
    const entityIds = Object.keys(hoursByEntity).filter(_ => !!hoursByEntity[_]);
    const [knownEntitiesMap, setKnownEntitiesMap] = React.useState<Dictionary<IEditableExtensibleEntityWithResourcePlan>>({});
    const [isExpanded, setIsExpanded] = React.useState(false);

    React.useEffect(() => {
        if (entityIds.length) {
            post<IEditableExtensibleEntityWithResourcePlan[]>(`api/resourcePlan/find`, { entityType, ids: entityIds })
                .then(entities => setKnownEntitiesMap(toDictionaryById(entities)))
                .catch(defaultCatch());
        }
    }, []);

    const hours = roundToHundreds(entityIds.reduce((cum, _) => cum + hoursByEntity[_], 0));
    const knownEntities = entityIds.map(_ => knownEntitiesMap[_]).filter(notUndefined);
    const unknownEntityIds = entityIds.filter(_ => !knownEntitiesMap[_]);
    const unknownEntitiesHours = roundToHundreds(unknownEntityIds.reduce((cum, _) => cum + hoursByEntity[_], 0));
    const singleEntity = entityIds.length === 1 ? knownEntities[0] : undefined;

    return <div className="work-by-entity">
        <div className="work-by-entity-summary">
            <div className="work-by-entity-total">
                <span className="progress-tip gray">{formatValue(hours)}</span> hour{hours !== 1 ? 's' : ''}
                {singleEntity
                    ? <> on {getEntityLabel(entityType, 1)} <Link href={`/${entityType}/${singleEntity.id}`} title={singleEntity.attributes.Name}>
                        {singleEntity.attributes.Name}
                    </Link></>
                    : entityIds.length > 0 && <> on {entityIds.length} {getEntityLabel(entityType, entityIds.length)}</>}
            </div>
            {entityIds.length > 1 && knownEntities.length > 0 && <ActionButton className="work-by-entity-toggle hyperlink-color"
                iconProps={{ iconName: isExpanded ? 'ChevronUpMed' : 'ChevronDownMed' }}
                text={isExpanded ? 'Less' : 'Details'}
                onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsExpanded(!isExpanded);
                }} />}
        </div>
        {isExpanded && <div className="work-by-entity-list">
            {knownEntities.map(_ => <div key={_.id} className="work-by-entity-item">
                <span className="hours">{formatValue(hoursByEntity[_.id])}h</span>
                <Link className="work-by-entity-name" href={`/${entityType}/${_.id}`} title={_.attributes.Name}>{_.attributes.Name}</Link>
            </div>)}
            {unknownEntityIds.length > 0 && <div className="work-by-entity-item">
                <span className="hours">{formatValue(unknownEntitiesHours)}h</span>
                <span>{unknownEntityIds.length} more {getEntityLabel(entityType, unknownEntityIds.length)}</span>
            </div>}
        </div>}
    </div>;
}

const getEntityLabel = (entityType: EntityType, count: number) =>
    (count === 1 ? entityTypeLabelMap[entityType].singular : entityTypeLabelMap[entityType].plural).toLowerCase();
